import { useState, useEffect, useMemo } from 'react';

import { UserApi } from '@/api';
import { getMarkerTypeKey } from '@/constants/mapMarkerKeys';
import { IUser } from '@/models';
import { useUserStore } from '@/store';

import { useMarkerTypes } from './useMarkerTypes';

export interface MapMarkerTypes {
  fireHydrant: boolean;
  pothole: boolean;
  roadBlock: boolean;
  flooding: boolean;
  limitedVisibility: boolean;
  deadAnimal: boolean;
  downWires: boolean;
  downPole: boolean;
  debris: boolean;
  openManhole: boolean;
  showInactive: boolean;
  roadWork: boolean;
  // Dynamic custom marker types will be added here
  [key: string]: boolean;
}

export const useMapMarkerTypes = () => {
  const { user, account, updateUserStore } = useUserStore();
  const { enabledMarkerTypes } = useMarkerTypes();
  const allowedTypes = account?.settings.features?.mapMarkerTypes;

  // Create stable default values using useMemo
  const defaultSelectedTypes = useMemo((): MapMarkerTypes => {
    const baseTypes: MapMarkerTypes = {
      fireHydrant:
        user?.settings?.mapLayersPreferences?.fireHydrant?.visible ??
        allowedTypes?.fireHydrant ??
        true,
      pothole:
        user?.settings?.mapLayersPreferences?.pothole?.visible ??
        allowedTypes?.pothole ??
        true,
      roadBlock:
        user?.settings?.mapLayersPreferences?.roadBlock?.visible ??
        allowedTypes?.roadBlock ??
        true,
      flooding:
        user?.settings?.mapLayersPreferences?.flooding?.visible ??
        allowedTypes?.flooding ??
        true,
      limitedVisibility:
        user?.settings?.mapLayersPreferences?.limitedVisibility?.visible ??
        allowedTypes?.limitedVisibility ??
        true,
      roadWork:
        user?.settings?.mapLayersPreferences?.roadWork?.visible ??
        allowedTypes?.roadWork ??
        true,
      deadAnimal:
        user?.settings?.mapLayersPreferences?.deadAnimal?.visible ??
        allowedTypes?.deadAnimal ??
        true,
      downWires:
        user?.settings?.mapLayersPreferences?.downWires?.visible ??
        allowedTypes?.downWires ??
        true,
      downPole:
        user?.settings?.mapLayersPreferences?.downPole?.visible ??
        allowedTypes?.downPole ??
        true,
      debris:
        user?.settings?.mapLayersPreferences?.debris?.visible ??
        allowedTypes?.debris ??
        true,
      openManhole:
        user?.settings?.mapLayersPreferences?.openManhole?.visible ??
        allowedTypes?.openManhole ??
        true,
      showInactive:
        user?.settings?.mapLayersPreferences?.showInactive?.visible ??
        allowedTypes?.showInactive ??
        false,
    };

    // Add custom marker types
    enabledMarkerTypes.forEach((markerType) => {
      const key = getMarkerTypeKey(markerType.name);
      if (!(key in baseTypes)) {
        baseTypes[key] =
          user?.settings?.mapLayersPreferences?.[key]?.visible ?? true;
      }
    });

    return baseTypes;
  }, [enabledMarkerTypes, user?.settings?.mapLayersPreferences, allowedTypes]);

  const [selectedTypes, setSelectedTypes] =
    useState<MapMarkerTypes>(defaultSelectedTypes);

  // Update selectedTypes when dependencies change, but only if necessary
  useEffect(() => {
    setSelectedTypes((prev) => {
      // Check if we need to update by comparing with new default values
      const newDefaults = defaultSelectedTypes;
      let needsUpdate = false;

      // Check for new custom marker types
      enabledMarkerTypes.forEach((markerType) => {
        const key = getMarkerTypeKey(markerType.name);
        if (!(key in prev)) {
          needsUpdate = true;
        }
      });

      // Only update if there are new marker types
      if (needsUpdate) {
        return { ...prev, ...newDefaults };
      }

      return prev;
    });
  }, [defaultSelectedTypes, enabledMarkerTypes]);

  const handleCheckboxChange = (key: keyof MapMarkerTypes) => {
    setSelectedTypes((prev) => {
      const newTypes = {
        ...prev,
        [key]: !prev[key],
      };

      // Transform boolean values to {visible: boolean} objects
      const transformedPreferences = Object.entries(newTypes).reduce(
        (acc, [k, value]) => {
          return {
            ...acc,
            [k]: { visible: value },
          };
        },
        {},
      );

      // Save preferences using the updated state
      UserApi.updateMapLayersPreferences(user?._id as string, {
        mapLayersPreferences: transformedPreferences,
      }).then(() => {
        updateUserStore({
          user: {
            ...(user as IUser),
            settings: {
              ...(user as IUser).settings,
              mapLayersPreferences: {
                ...transformedPreferences,
              },
            },
          },
        });
      });

      return newTypes;
    });
  };

  return { selectedTypes, handleCheckboxChange };
};
